import { FlagIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';

import { ShiftDetailActivity } from '../api/types';

import Avatar from './Avatar';

// One row of the shift activity feed. Shared by the Shift Detail panel's
// Recent activity preview and the "View all" modal (GRAVY-10294) — the modal is
// opened from the preview, so a row that changed shape on the way in would read
// as a different event. Both surfaces render THIS component, which is what makes
// "apply to all recent/activity log UI" (GRAVY-10345) one change rather than two.

/*
 * `relativeDay` ("Today" / "Yesterday" / a date) lived here and is gone with
 * GRAVY-10358 §C1: it was the fallback `elapsedLabel` used past a week, and the
 * new table has no band it could serve — 24–47 hours is "1 day ago" and
 * everything older is the calendar date, with a year where the year differs.
 * Nothing else imported it.
 */

/**
 * The row's primary timestamp — Product's table, verbatim (GRAVY-10358 §C1):
 *
 *   0–59 sec            Just now
 *   1–59 min            N min ago
 *   1–23 hr             N hr ago
 *   24–47 hr            1 day ago
 *   48 hr – same year   Aug 14
 *   48 hr – other year  Aug 14, 2025
 *
 * Two rules that are easy to get wrong and are asserted separately:
 *
 *   ROUND DOWN, never up. 89 seconds is "1 min ago", not "2 min ago" —
 *   `Math.floor` throughout, so the label never claims more elapsed time than
 *   has actually passed.
 *
 *   The UNIT is the abbreviation and does not inflect: "1 min ago" and
 *   "5 min ago". The previous build wrote "1 mins ago" for a whole minute and
 *   "15 mins ago" thereafter, which is the pluralisation the ticket calls out.
 *
 * What changed beyond the wording: 2–6 days used to read "N days ago". Only
 * 24–47 hours is a "day" now; past that the calendar date is what a reader can
 * actually place, and past a year it needs the year to be unambiguous.
 *
 * The exact moment is never lost — see `exactTime`, which every row exposes on
 * hover.
 */
export const elapsedLabel = (
  iso?: string | null,
  now: Date = new Date(),
): string => {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';

  const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
  // A clock skew between server and browser must not print "in 3 min".
  if (seconds < 60) return 'Just now';

  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins} min ago`;

  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;

  // 24–47 hours only. From 48 the date is what places the event.
  if (hrs < 48) return '1 day ago';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    // Older than the current year, so "Aug 14" would be ambiguous. Compared on
    // the YEAR rather than on a 365-day span: two dates four months apart can
    // straddle New Year, and "Aug 14" for last August is exactly the ambiguity
    // the year is there to remove.
    ...(then.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  }).format(then);
};

/**
 * The full moment, always available on hover (GRAVY-10358 §C1) — including for
 * "Just now" and "5 min ago", which is the point: a relative label is easier to
 * read and useless for a dispute, and the exact time is the opposite.
 *
 * "Aug 14, 2025, 4:15 PM PDT" — the viewer's own zone, NAMED. Without
 * `timeZoneName` an admin in another region reads a time that looks local and
 * is not, which is the one way this can mislead rather than merely omit.
 */
export const exactTime = (iso?: string | null): string | undefined => {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(d);
};

/** How much of a message body shows before "show more". */
const QUOTE_PREVIEW = 60;

type Segment = NonNullable<ShiftDetailActivity['segments']>[number];

type Props = {
  item: ShiftDetailActivity;
  /** Opens a worker's profile — a linked name in the sentence. */
  onSelectWorker?: (workerId: number) => void;
  /** Opens the invited-worker list behind an "N workers" segment. */
  onSelectWorkers?: (workerIds: number[], names: string[]) => void;
};

/**
 * The sentence, rendered from the server's pieces.
 *
 * Every word comes from `segments`; this decides only how each piece LOOKS and
 * what it does. Where a segment needs a handler the caller did not supply, it
 * degrades to plain emphasis rather than a dead control.
 */
const Sentence = ({
  segments,
  onSelectWorker,
  onSelectWorkers,
}: {
  segments: Segment[];
  onSelectWorker?: (workerId: number) => void;
  onSelectWorkers?: (workerIds: number[], names: string[]) => void;
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <span className="dashv2-panel-acttext" data-testid="activitySentence">
      {segments.map((seg, i) => {
        // Segments have no ids — they are positional pieces of one sentence —
        // so the index IS the identity here.
        const key = `${seg.type}-${i}`;
        // Pieces are joined with a space, EXCEPT before punctuation: a list of
        // changed fields emits ", slots", and " , slots" is not a sentence.
        const space = i > 0 && !/^[,.;:!?]/.test(seg.text) ? ' ' : '';

        if (seg.type === 'actor') {
          return (
            <span key={key} className="dashv2-panel-actactor">
              {space}
              {seg.text}
            </span>
          );
        }

        if (seg.type === 'strong') {
          return (
            <span key={key} className="dashv2-panel-actstrong">
              {space}
              {seg.text}
            </span>
          );
        }

        if (seg.type === 'worker' && seg.worker_id != null && onSelectWorker) {
          return (
            <span key={key}>
              {space}
              <button
                type="button"
                className="dashv2-panel-actlink"
                data-testid="activityWorkerLink"
                onClick={() => onSelectWorker(seg.worker_id as number)}
              >
                {seg.text}
              </button>
            </span>
          );
        }

        if (
          seg.type === 'workers' &&
          seg.worker_ids?.length &&
          onSelectWorkers
        ) {
          return (
            <span key={key}>
              {space}
              <button
                type="button"
                className="dashv2-panel-actlink"
                data-testid="activityWorkersLink"
                onClick={() =>
                  onSelectWorkers(seg.worker_ids as number[], seg.names ?? [])
                }
              >
                {seg.text}
              </button>
            </span>
          );
        }

        if (seg.type === 'quote') {
          const long = seg.text.length > QUOTE_PREVIEW;
          const shown =
            long && !expanded
              ? `${seg.text.slice(0, QUOTE_PREVIEW)}…`
              : seg.text;
          return (
            <span key={key}>
              {space}
              <span
                className="dashv2-panel-actquote"
                data-testid="activityQuote"
              >
                “{shown}”
              </span>
              {long && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="dashv2-panel-actmore"
                    data-testid="activityQuoteToggle"
                    aria-expanded={expanded}
                    onClick={() => setExpanded((open) => !open)}
                  >
                    {expanded ? 'show less' : 'show more'}
                  </button>
                </>
              )}
            </span>
          );
        }

        // `text`, and any segment whose interactive form is unavailable —
        // rendered as words rather than dropped, so the sentence stays whole.
        return (
          <span key={key}>
            {space}
            {seg.text}
          </span>
        );
      })}
    </span>
  );
};

/**
 * WHOSE face belongs on the row (GRAVY-10350 A1/A2, corrected by GRAVY-10358 §A),
 * in precedence:
 *
 *   automated       → the GravyWork glyph (handled by the caller)
 *   an admin acted  → the ADMIN's photo, or the ADMIN's initials
 *   otherwise       → the subject worker's photo, or their initials
 *
 * ## The bug this fixes
 *
 * The src was `actor_avatar_url || avatar_url`. Those are two different people:
 * the first is the admin who acted, the second is the worker the sentence is
 * about. So an admin with no profile photo did not fall back to their own
 * initials — they fell back to the WORKER'S PHOTO, and "Richard R. removed
 * Marcus Rivera" showed a picture of Marcus. Not a missing avatar: a wrong one,
 * which is worse, because nothing about it looks broken.
 *
 * The fix is that the two are chosen together. Once an admin is named, the row
 * is theirs — photo if they have one, their initials if not — and the worker's
 * photo is never borrowed for it.
 *
 * `worker_name` is why the initials are right in the second case. The client
 * previously fell back to `title` ("Marcus Rivera checked in"), whose first two
 * words happen to be the name; on a title like "Shift completed" it produced
 * "SC".
 */
const faceFor = (item: ShiftDetailActivity) =>
  item.actor_name
    ? { name: item.actor_name, src: item.actor_avatar_url ?? null }
    : {
        name: item.worker_name || item.title || '',
        src: item.avatar_url ?? null,
      };

const ActivityItem = ({ item, onSelectWorker, onSelectWorkers }: Props) => {
  const segments = item.segments ?? [];
  const actorFace = faceFor(item);

  return (
    <div className="dashv2-panel-actitem">
      <div className="dashv2-panel-actrow">
        {item.automated ? (
          <span className="dashv2-panel-actbot" aria-hidden="true" />
        ) : (
          <Avatar name={actorFace.name} src={actorFace.src} />
        )}

        {segments.length > 0 ? (
          <Sentence
            segments={segments}
            onSelectWorker={onSelectWorker}
            onSelectWorkers={onSelectWorkers}
          />
        ) : (
          // No sentence defined for this event type — the flat form, which is
          // still every word the server resolved.
          <span className="dashv2-panel-acttext">
            {item.title || item.event_type}
            {item.detail ? ` — ${item.detail}` : ''}
          </span>
        )}

        {/* The check-in or check-out happened outside the client's own radius
            (GRAVY-10358 §B, Figma 39545:146616). Only ever rendered for `true`:
            `null` means no coordinates were recorded, and "we do not know" must
            not be drawn as "we checked and it was fine". */}
        {item.location_flagged === true && (
          <span className="dashv2-panel-actflag" data-testid="activityFlagged">
            <FlagIcon aria-hidden="true" />
            Flagged
          </span>
        )}
      </div>

      <div className="dashv2-panel-actmeta">
        <span title={exactTime(item.occurred_at)} data-testid="activityWhen">
          {elapsedLabel(item.occurred_at)}
        </span>
        <span className="dashv2-panel-dot" />
        <span>{item.event_type}</span>
      </div>
    </div>
  );
};

export default ActivityItem;
